import AttractMode from './control_modes/attract'
import ModeSwitcher from '../../render/mode-switcher'
import MouseMode from './control_modes/mouse'
import KeyboardMode from './control_modes/keyboard'
import {RenderEventDispatcher} from './event-dispatcher'
import {Box3, Vector3} from 'three'
import type {PerspectiveCamera, WebGLRenderer} from 'three'
import {logger} from '../../render/setup-globals'
import type ThreeDViewer from './viewer'
import type Renderable from './renderable'
import type {RenderableObject} from '3d'
import type {NullRenderer} from './null-renderer'

type ControlModes = 'attract' | 'mouse'
type ControlEvent = {
  type: 'tick'
  tick: {
    data: number
  }
}

// TODO: when a pause icon enters Octicons and we upgrade, use Octicons instead
// https://github.com/primer/octicons/pull/1070
const enableAutoRotateText = '▶️ Start auto-rotation'
const disableAutoRotateText = '⏸️ Stop auto-rotation'
const autoRotateButtonHtmlId = 'auto-rotation-button'
/**
 * Controls provides the camera controls interface to the 3D object viewer.
 */
class Controls extends RenderEventDispatcher<ControlEvent> {
  viewer: ThreeDViewer
  renderer: WebGLRenderer | NullRenderer
  camera: PerspectiveCamera
  // Standard quanta of rotation. Roughly about one degree.
  // Used by control modes to increment the orbit position.
  orbitIncrement: number = Math.PI / 180
  center: Vector3 = new Vector3(0, 0, 0)
  container: HTMLElement | null = null
  modes: {
    attract: AttractMode
    mouse: MouseMode
    keyboard: KeyboardMode
  }
  autoRotateEnabled: boolean

  constructor(viewer: ThreeDViewer) {
    super()

    this.viewer = viewer
    this.viewer.addEventListener('added', this.updateCenter)
    this.viewer.addEventListener('tick', this.update)

    this.renderer = this.viewer.renderer
    this.camera = this.viewer.camera!

    this.container = this.renderer.domElement

    const distance = this.orbitDistance()
    this.camera.position.set(0, -(distance / 2), distance).setLength(distance)

    this.modes = {
      attract: new AttractMode(this, true),
      mouse: new MouseMode(this, false, {onUse: this.#activateMouseMode, onRelease: this.#activateAttractMode}),
      keyboard: new KeyboardMode(this, false, {
        onUse: this.#activateKeyboardMode,
        onRelease: this.#activateAttractMode,
      }),
    }

    this.autoRotateEnabled = false

    this.initModeMenu()
    this.updateCenter()
    document.getElementById(autoRotateButtonHtmlId)?.addEventListener('click', this.#toggleAutoRotate)
  }

  // Utils
  containerEl() {
    if (!this.container) {
      this.container = this.renderer.domElement
    }
    return this.container
  }

  selectMode = (mode: ControlModes) => {
    logger.debug(`Switching to ${mode} mode`)
    const objects = this.viewer.objects as Record<string, Renderable>
    for (const name of Object.keys(objects)) {
      const obj = objects[name]
      obj.switchMode(mode)
    }
  }

  #toggleAutoRotate = (e: MouseEvent) => {
    this.autoRotateEnabled = !this.autoRotateEnabled
    const autoRotateButton = e.target as HTMLElement
    autoRotateButton.innerText = this.autoRotateEnabled ? disableAutoRotateText : enableAutoRotateText
    if (this.autoRotateEnabled) {
      this.#activateAttractMode()
    } else {
      this.modes.attract.deactivate()
    }
  }

  #activateMouseMode = () => {
    for (const name of Object.keys(this.modes)) {
      const mode = this.modes[name as ControlModes]
      mode.deactivate()
    }
    this.modes.mouse.activate()
  }

  #activateKeyboardMode = () => {
    for (const name of Object.keys(this.modes)) {
      const mode = this.modes[name as ControlModes]
      mode.deactivate()
    }
    this.modes.keyboard.activate()
  }

  #activateAttractMode = () => {
    // We only want this to fire if the user has explicitly allowed
    // auto-rotation
    if (!this.autoRotateEnabled) return
    for (const name of Object.keys(this.modes)) {
      const mode = this.modes[name as ControlModes]
      mode.deactivate()
    }

    this.modes.attract.activate()
  }

  initModeMenu = () => {
    return new ModeSwitcher<ControlModes>(this.selectMode)
  }

  orbitDistance = () => {
    const zoom = 5
    const radius = (object: Renderable) => {
      return (object.geometry.boundingSphere && object.geometry.boundingSphere.radius) ?? 0
    }
    const viewerObjects = (this.viewer.objects ?? {}) as Record<string, Renderable>
    const voKeys = Object.keys(viewerObjects)
    const objectRadii = voKeys.map<number>(name => radius(viewerObjects[name] ?? 0))

    const max = Math.max(...objectRadii)
    if (max > 0) {
      return max * zoom
    } else {
      return 100
    }
  }

  // Yeild to fn after offsetting `object` by `-offeset` first
  // then calling `fn` then adding back the offset vector as
  // `+offset` to `object.position` to make orbital rotations
  // and length scaling of camera position easier.
  withoutOriginOffset(camera: PerspectiveCamera, offset: Vector3, fn: () => void) {
    camera.position.sub(offset)
    fn()
    camera.position.add(offset)
  }

  // Direct camera controls
  orbitRight = (angle: number) => {
    this.withoutOriginOffset(this.camera, this.center, () => {
      const axis = new Vector3(0, 0, 1)
      this.camera.position.applyAxisAngle(axis, angle)
      this.camera.up.applyAxisAngle(axis, angle)
    })
  }

  orbitUp = (angle: number) => {
    this.withoutOriginOffset(this.camera, this.center, () => {
      const axis = this.camera.position.clone().cross(this.camera.up).normalize()
      this.camera.position.applyAxisAngle(axis, angle)
      this.camera.up.applyAxisAngle(axis, angle)
    })
  }

  translateUp = (change: number) => {
    const translation = this.camera.up.clone().normalize().multiplyScalar(change)
    this.camera.position.add(translation)
    this.center.add(translation)
  }

  translateLeft = (change: number) => {
    const up = this.camera.up.clone().normalize()
    const position = this.camera.position.clone().normalize()
    const translation = up.cross(position).multiplyScalar(change)
    this.camera.position.add(translation)
    this.center.add(translation)
  }

  changeZoom = (delta: number) => {
    this.withoutOriginOffset(this.camera, this.center, () => {
      if (delta > 0) {
        this.camera.position.multiplyScalar(1.02)
      } else if (delta < 0) {
        this.camera.position.multiplyScalar(0.98)
      }
    })
  }

  // Sign inverting camera controls
  orbitLeft = (angle: number) => {
    return this.orbitRight(-angle)
  }
  orbitDown = (angle: number) => {
    return this.orbitUp(-angle)
  }
  translateDown = (change: number) => {
    return this.translateUp(-change)
  }
  translateRight = (change: number) => {
    return this.translateLeft(-change)
  }

  // Handlers
  update = () => {
    this.camera.lookAt(this.center)
    this.dispatchEvent({type: 'tick', data: 0})
  }

  updateCenter = () => {
    let bounds: Box3 | undefined = undefined
    this.viewer!.scene!.traverse((o: RenderableObject) => {
      if (o.mesh) {
        return
      }

      if (!o.geometry) {
        return
      }

      const geometry = o.geometry

      if (!geometry.boundingBox) {
        return
      }

      geometry.computeBoundingBox()

      if (!bounds) {
        bounds = geometry.boundingBox.clone()
      } else {
        bounds.expandByPoint(geometry.boundingBox.min)
        bounds.expandByPoint(geometry.boundingBox.max)
      }
    })

    if (bounds) {
      this.center = (bounds as Box3).getCenter(new Vector3()).clone()
    }

    logger.debug('Center updated, now:', this.center)
  }
}

export default Controls
